/**
 * Message patterns that identify a failed dynamic import of a content-hashed
 * chunk.
 *
 * Every JS engine words this differently, and the wording is the ONLY signal
 * available — the error is a plain `TypeError` with no status, code, or
 * structured field to key off. Missing one engine's phrasing silently
 * misclassifies that engine's users, which is exactly how BUMBLEBEE-B4 leaked:
 * the list covered Chromium, Firefox and webpack but not WebKit, so Safari
 * users got the generic "something went wrong" screen after every deploy and
 * the expected deploy churn was filed as a frontend defect.
 *
 * Matching on wording cuts both ways, so each pattern has to be specific enough
 * not to swallow an unrelated failure: this helper decides what `beforeSend`
 * drops, and it sees every error in the app.
 */
const CHUNK_LOAD_ERROR_PATTERNS = [
  // Chromium: "Failed to fetch dynamically imported module: <url>"
  // Firefox:  "error loading dynamically imported module: <url>"
  /dynamically imported module/i,
  // WebKit/Safari: "Importing a module script failed." — mentions neither
  // "fetch" nor "module" in the shape the other engines use.
  /module script failed/i,
  // Vite's own preload helper, when a chunk's stylesheet dependency 404s:
  // "Unable to preload CSS for <dep>"
  /unable to preload css for/i,
  // webpack-era wording ("Loading chunk 123 failed"), kept for any bundle not
  // produced by Vite. Anchored on the whole shape rather than a bare "loading
  // chunk" substring, which is ordinary enough English to also match unrelated
  // stream and upload failures ("error loading chunk data from S3").
  /loading (css )?chunk \S+ failed/i,
];

/**
 * Checks if an error is a chunk/module loading error.
 *
 * These errors occur when a dynamically imported module fails to load,
 * typically after a deployment: the app ships as a single nginx image, so the
 * previous build's `assets/*-<hash>.js` files stop existing the moment the new
 * pod serves traffic. A session still running the old module graph then 404s on
 * a route chunk it had not downloaded yet.
 *
 * This is expected deploy churn rather than a defect — it is recovered by
 * reloading (see the `vite:preloadError` listener in `src/main.tsx`, and the
 * reload prompt in `RouteErrorBoundary`) and is dropped in Sentry's
 * `beforeSend`.
 *
 * A raw message string is accepted too: reports with no error object
 * (message-only `window.onerror`, non-Error rejections) carry the wording on
 * the event instead.
 *
 * @param error - The error (or raw error message) to check
 * @returns True if the error is a chunk loading error
 */
export function isChunkLoadError(error: unknown): boolean {
  if (typeof error === 'string') {
    return matchesChunkLoadWording(error);
  }

  if (!(error instanceof Error)) {
    return false;
  }

  if (error.name === 'ChunkLoadError') {
    return true;
  }

  return matchesChunkLoadWording(error.message);
}

function matchesChunkLoadWording(message: string): boolean {
  return CHUNK_LOAD_ERROR_PATTERNS.some(pattern => pattern.test(message));
}
